import React, { useState } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { SUBMIT_CONTACT } from '../graphql/operations';
import UspStrip from '../components/UspStrip';
import Footer from '../components/Footer';
import ContactMap from '../components/ContactMap';

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const [submitContact, { loading }] = useMutation(SUBMIT_CONTACT);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrorMsg('Please fill in Name, Email and Message.');
      return;
    }
    setErrorMsg('');
    try {
      const { data } = await submitContact({
        variables: {
          input: {
            name: form.name,
            email: form.email,
            phone: form.phone,
            message: form.message,
          },
        },
      });
      if (data?.submitContact?.success) {
        setSubmitted(true);
        setForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitted(false), 4000);
      } else {
        setErrorMsg(data?.submitContact?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="page-fade d-flex flex-column" style={{ minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ flex: 1 }}>
        <div className="breadcrumb-bar">
          <Link to="/">Home</Link> &gt; Contact Us
        </div>

        {/* Page Hero */}
        <div style={{
          background: 'linear-gradient(135deg, #fff5fb 0%, #fdf2f8 50%, #ffffff 100%)',
          padding: '60px 5%',
        }}>
          <Container>
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>
                Contact <span style={{ background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Us</span>
              </h2>
              <div style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', borderRadius: '2px', margin: '20px auto' }}></div>
              <p className="lead text-muted mt-4" style={{ maxWidth: '600px', margin: '20px auto', fontSize: '15px' }}>
                Have questions? We'd love to hear from you. Get in touch with our team today!
              </p>
            </div>
          </Container>
        </div>

        {/* Contact Content */}
        <Container className="py-5">
          <Row className="g-4">
            {/* Contact Info */}
            <Col md={12} lg={4}>
              <div style={{
                background: '#fff',
                borderRadius: '14px',
                border: '1px solid #eaeef5',
                padding: '32px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <h4 className="fw-bold text-dark mb-4" style={{ fontSize: 'clamp(16px, 4vw, 18px)', letterSpacing: '-0.3px' }}>
                  Get In Touch
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  {[
                    {
                      icon: 'fa-map-marker-alt',
                      title: 'Address',
                      content: 'NO:42, J.V.R Nagar Complex, N.S.R Road, Nehru Nagar, Kumaran Kundram, Chromepet, Tamil Nadu – 600044, India',
                      color: '#e91e8c',
                    },
                    { icon: 'fa-phone', title: 'Phone', content: '+91 91508 08130', color: '#3b5bdb' },
                    {
                      icon: 'fab fa-whatsapp',
                      title: 'WhatsApp',
                      content: '+91 91508 08130',
                      link: 'https://wa.me/919150808130',
                      color: '#25d366',
                    },
                    { icon: 'fa-envelope', title: 'Email', content: 'pdp@prathithadp.com', color: '#ff6b6b' },
                    {
                      icon: 'fa-clock',
                      title: 'Working Hours',
                      content: 'Mon - Sat: 9:30 AM – 6:00 PM\nSunday: Closed',
                      color: '#10b981',
                    },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      onMouseEnter={() => setHoveredItemIndex(idx)}
                      onMouseLeave={() => setHoveredItemIndex(null)}
                      style={{
                        display: 'flex',
                        gap: '14px',
                        padding: '16px',
                        borderRadius: '10px',
                        background: '#fafbfe',
                        border: '1px solid #eaeef5',
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredItemIndex === idx ? 'translateX(6px)' : 'translateX(0)',
                        boxShadow: hoveredItemIndex === idx
                          ? `0 8px 20px rgba(${item.color === '#e91e8c' ? '233, 30, 140' : item.color === '#3b5bdb' ? '59, 91, 219' : item.color === '#25d366' ? '37, 211, 102' : item.color === '#ff6b6b' ? '255, 107, 107' : '16, 185, 129'}, 0.12)`
                          : 'none',
                      }}
                    >
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          background: `${item.color}12`,
                          color: item.color,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontSize: '16px',
                          transition: 'all 0.3s ease',
                          transform: hoveredItemIndex === idx ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0)',
                        }}
                      >
                        <i className={`fas ${item.icon}`}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h6 style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#1a1a1a',
                          margin: '0 0 4px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}>
                          {item.title}
                        </h6>
                        {item.link ? (
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: item.color,
                              fontWeight: '600',
                              textDecoration: 'none',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.gap = '6px'}
                            onMouseLeave={(e) => e.currentTarget.style.gap = '4px'}
                          >
                            {item.content}
                            <i className="fas fa-external-link-alt" style={{ fontSize: '9px', flexShrink: 0 }}></i>
                          </a>
                        ) : (
                          <p style={{
                            fontSize: '12px',
                            color: '#666',
                            margin: 0,
                            lineHeight: '1.5',
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                          }}>
                            {item.content}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            {/* Contact Form */}
            <Col md={12} lg={4}>
              <div style={{
                background: '#fff',
                borderRadius: '14px',
                border: '1px solid #eaeef5',
                padding: '32px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <h4 className="fw-bold text-dark mb-4" style={{ fontSize: 'clamp(16px, 4vw, 18px)', letterSpacing: '-0.3px' }}>
                  Send Your Enquiry
                </h4>

                <div className="mb-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: focusedField === 'name' ? '2px solid #e91e8c' : '1px solid #eaeef5',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      transition: 'all 0.3s ease',
                      boxShadow: focusedField === 'name' ? '0 6px 16px rgba(233, 30, 140, 0.15)' : 'none',
                      outline: 'none',
                      backgroundColor: focusedField === 'name' ? '#fff5fb' : '#fafbfe',
                    }}
                  />
                </div>

                <div className="mb-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={form.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: focusedField === 'email' ? '2px solid #e91e8c' : '1px solid #eaeef5',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      transition: 'all 0.3s ease',
                      boxShadow: focusedField === 'email' ? '0 6px 16px rgba(233, 30, 140, 0.15)' : 'none',
                      outline: 'none',
                      backgroundColor: focusedField === 'email' ? '#fff5fb' : '#fafbfe',
                    }}
                  />
                </div>

                <div className="mb-4">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number (Optional)"
                    value={form.phone}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: focusedField === 'phone' ? '2px solid #e91e8c' : '1px solid #eaeef5',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      transition: 'all 0.3s ease',
                      boxShadow: focusedField === 'phone' ? '0 6px 16px rgba(233, 30, 140, 0.15)' : 'none',
                      outline: 'none',
                      backgroundColor: focusedField === 'phone' ? '#fff5fb' : '#fafbfe',
                    }}
                  />
                </div>

                <div className="mb-4">
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: focusedField === 'message' ? '2px solid #e91e8c' : '1px solid #eaeef5',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      transition: 'all 0.3s ease',
                      boxShadow: focusedField === 'message' ? '0 6px 16px rgba(233, 30, 140, 0.15)' : 'none',
                      outline: 'none',
                      backgroundColor: focusedField === 'message' ? '#fff5fb' : '#fafbfe',
                      resize: 'none',
                    }}
                  />
                </div>

                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: 'clamp(13px, 2.5vw, 14px)',
                      fontWeight: '700',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                      color: '#fff',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 6px 16px rgba(233, 30, 140, 0.3)',
                      opacity: loading ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(233, 30, 140, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(233, 30, 140, 0.3)';
                    }
                  }}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>

                {submitted && (
                  <div style={{
                    padding: '12px 14px',
                    background: '#ecfdf5',
                    border: '1px solid #10b981',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#10b981',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <i className="fas fa-check-circle"></i>
                    Message sent successfully! We'll get back to you soon.
                  </div>
                )}

                {errorMsg && (
                  <div style={{
                    padding: '12px 14px',
                    background: '#fef2f2',
                    border: '1px solid #ff6b6b',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#ff6b6b',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <i className="fas fa-exclamation-circle"></i>
                    {errorMsg}
                  </div>
                )}
                </div>
              </div>
            </Col>

            {/* Map Column */}
            <Col md={12} lg={4}>
              <div style={{
                background: '#fff',
                borderRadius: '14px',
                border: '1px solid #eaeef5',
                padding: '32px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <h4 className="fw-bold text-dark mb-4" style={{ fontSize: 'clamp(16px, 4vw, 18px)', letterSpacing: '-0.3px' }}>
                  Find Us
                </h4>
                <div style={{
                  flex: 1,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid #eaeef5',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                }}>
                  <ContactMap />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <UspStrip />
      <Footer />
    </div>
  );
};

export default ContactPage;